export default function TeacherEnablement() {
  const supportFramework = [
    {
      title: 'Initial Training',
      subtitle: '40 Hours',
      description: 'Comprehensive onboarding program covering curriculum, equipment, and teaching methodologies',
      icon: 'ri-graduation-cap-line',
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Ongoing Workshops',
      subtitle: 'Monthly',
      description: 'Regular professional development sessions on new technologies and teaching strategies',
      icon: 'ri-calendar-event-line',
      color: 'from-green-500 to-green-600'
    },
    {
      title: 'Resource Library',
      subtitle: '1000+ Resources',
      description: 'Access to lesson plans, activity guides, videos, and assessment materials',
      icon: 'ri-book-open-line',
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'Peer Community',
      subtitle: '500+ Teachers',
      description: 'Connect with fellow educators, share experiences, and collaborate on projects',
      icon: 'ri-team-line',
      color: 'from-orange-500 to-orange-600'
    },
    {
      title: 'Certification Programs',
      subtitle: 'Professional Growth',
      description: 'Earn recognized certifications in STEM education and specialized technologies',
      icon: 'ri-award-line',
      color: 'from-pink-500 to-pink-600'
    },
    {
      title: '24/7 Support',
      subtitle: 'Always Available',
      description: 'Technical and pedagogical support through helpdesk, chat, and phone',
      icon: 'ri-customer-service-2-line',
      color: 'from-teal-500 to-teal-600'
    }
  ];

  const testimonials = [
    {
      name: 'Mrs. Kavita Sharma',
      subject: 'Science Teacher',
      school: 'Delhi Public School',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Indian%20female%20teacher%20portrait%2C%20confident%20educator%20in%20traditional%20attire%2C%20warm%20smile%2C%20modern%20classroom%20background%2C%20professional%20headshot&width=100&height=100&seq=teacher-001&orientation=squarish',
      quote: 'The training program transformed my teaching approach. I now feel confident integrating STEM activities into every lesson.',
      rating: 5
    },
    {
      name: 'Mr. Rajesh Kumar',
      subject: 'Computer Science Teacher',
      school: 'Ryan International',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Indian%20male%20teacher%20portrait%2C%20experienced%20educator%20in%20formal%20attire%2C%20confident%20expression%2C%20school%20setting%20background%2C%20professional%20headshot&width=100&height=100&seq=teacher-002&orientation=squarish',
      quote: 'The ongoing support and resource library have been invaluable. My students are more engaged than ever before.',
      rating: 5
    },
    {
      name: 'Ms. Priya Patel',
      subject: 'Mathematics Teacher',
      school: 'Kendriya Vidyalaya',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Indian%20female%20teacher%20portrait%2C%20young%20educator%20with%20glasses%2C%20friendly%20demeanor%2C%20educational%20environment%20background%2C%20professional%20headshot&width=100&height=100&seq=teacher-003&orientation=squarish',
      quote: 'Being part of the teacher community has helped me grow professionally. The monthly workshops keep me updated with latest trends.',
      rating: 5
    }
  ];

  return (
    <section id="teacher-training" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Empowering Educators to Lead the STEM Revolution
          </h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            Comprehensive training and continuous support to ensure teacher success and confidence
          </p>
        </div>

        {/* Support Framework - Hub and Spoke Visualization */}
        <div className="relative mb-24">
          <div className="max-w-5xl mx-auto">
            {/* Center Hub */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
              <div className="w-36 h-36 bg-gradient-to-br from-blue-600 to-blue-500 rounded-full flex items-center justify-center shadow-2xl">
                <div className="text-center text-white">
                  <i className="ri-user-star-line text-3xl mb-1"></i>
                  <div className="text-sm font-bold">Teacher Success</div>
                </div>
              </div>
            </div>

            {/* Spokes */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 pt-32 pb-32">
              {supportFramework.map((item, idx) => (
                <div
                  key={idx}
                  className="relative"
                  style={{
                    gridColumn: idx >= 3 ? 'span 1' : 'span 1'
                  }}
                >
                  <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-200 hover:shadow-2xl transition-all hover:-translate-y-2">
                    <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-4 mx-auto`}>
                      <i className={`${item.icon} text-3xl text-white`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 text-center mb-1">{item.title}</h3>
                    <div className="text-sm font-semibold text-blue-600 text-center mb-3">{item.subtitle}</div>
                    <p className="text-sm text-gray-600 text-center leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials Carousel */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-10">
            What Teachers Say
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-gray-200"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-blue-500 flex-shrink-0">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-blue-600 font-semibold">{testimonial.subject}</p>
                    <p className="text-sm text-gray-600">{testimonial.school}</p>
                  </div>
                </div>
                <p className="text-gray-700 italic mb-4 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex gap-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Training Program Details */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-12">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-10">
            Comprehensive Training Program
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-3">
                <i className="ri-time-line text-blue-600 text-xl"></i>
                Initial Training (40 Hours)
              </h4>
              <ul className="space-y-3">
                {[
                  'STEM pedagogy and teaching methodologies',
                  'Equipment operation and safety protocols',
                  'Curriculum mapping and lesson planning',
                  'Assessment strategies and rubrics',
                  'Classroom management for hands-on learning',
                  'Digital tools and LMS navigation'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-3">
                <i className="ri-refresh-line text-purple-600 text-xl"></i>
                Ongoing Support
              </h4>
              <ul className="space-y-3">
                {[
                  'Monthly professional development workshops',
                  'Quarterly curriculum updates and new activities',
                  'Access to 1000+ lesson plans and resources',
                  'Peer collaboration platform',
                  'Technical support helpdesk (24/7)',
                  'Annual refresher training sessions'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
